import { defineMutation } from './defineMutation';
import type {
  RegimeAnalysisAttributeDTO,
  RegimeAnalysisAttributeResponseDTO,
  RegimeSignalOptionsResponseDTO,
  RegimeSignalProfileRequestDTO,
  RegimeSignalProfileResponseDTO,
} from '@/api-v2/web/regime-analysis';
import {
  deleteRegime,
  getRegimes,
  getSignalOptions,
  getSignalProfile,
  postRegime,
  previewRegimeOnPortfolio,
  previewRegimeOnStrategy,
  updateRegime,
} from '@/api-v2/web/regime-analysis';
import type { LoadingWhenEnabledQRT, VQQueryOptions } from '@/types/VueQueryTypes';
import type { UseQueryReturnType } from '@tanstack/vue-query';
import { useQuery } from '@tanstack/vue-query';
import type { Ref } from 'vue';
import { computed } from 'vue';
import type { IPortfolioTreeTrackResponse } from '@/types/IPortfolioTreeTracksResponse';
import { chainEnabled, unwrap } from '@/utils/queries';
import type { IPortfolioTreeTracksQuery } from '@/types/IPortfolioTreeTracksQuery';
import { enforceMinimumDate } from '@/utils/dateUtils';
import type { ITrackQuery } from '@/types/ITrackQuery';
import type { StrategyTrackResponseDTO } from '@/api-v2/web/strategies';
import { RegimeAnalysisMethodConstants } from '@/constants/RegimeAnalysisMethodConstants';
import useLoadingWhenEnabledQuery from './useLoadingWhenEnabledQuery';

const keys = {
  all: () => [{ scope: 'regime-analysis' }] as const,
  list: () => [{ ...keys.all()[0], entity: 'list' }] as const,
  signalOptions: () => [{ ...keys.all()[0], entity: 'signal-options' }] as const,
  signalProfile: (request: Ref<RegimeSignalProfileRequestDTO | null>) =>
    [{ ...keys.all()[0], entity: 'signal-profile', request }] as const,
  previewStrategy: (
    track: Ref<{ code: string; query: ITrackQuery } | null>,
    attribute: Ref<RegimeAnalysisAttributeDTO | null>,
  ) => [{ ...keys.all()[0], entity: 'preview-strategy', track, attribute }] as const,
  previewPortfolio: (
    track: Ref<{ slug: string; query: IPortfolioTreeTracksQuery } | null>,
    attribute: Ref<RegimeAnalysisAttributeDTO | null>,
  ) => [{ ...keys.all()[0], entity: 'preview-portfolio', track, attribute }] as const,
};

const select = {
  regimeSets: (data: RegimeAnalysisAttributeResponseDTO[]): RegimeAnalysisAttributeResponseDTO[] => {
    return [...data].sort((a, b) => a.attribute.name.localeCompare(b.attribute.name));
  },
};

export const usePreviewRegimeOnStrategy = (
  track: Ref<{
    code: string;
    query: ITrackQuery;
  } | null>,
  attribute: Ref<RegimeAnalysisAttributeDTO | null>,
  options: VQQueryOptions<StrategyTrackResponseDTO> = {},
): LoadingWhenEnabledQRT<StrategyTrackResponseDTO, Error> => {
  const enabled = chainEnabled(
    options.enabled,
    computed(
      () =>
        track.value !== null &&
        !!attribute.value &&
        attribute.value.regime.method !== RegimeAnalysisMethodConstants.DATE,
    ),
    enforceMinimumDate(track.value?.query),
  );
  return useLoadingWhenEnabledQuery(
    useQuery<StrategyTrackResponseDTO, Error>({
      queryKey: keys.previewStrategy(track, attribute),
      queryFn: ({ signal }) =>
        previewRegimeOnStrategy({
          code: unwrap(track.value).code,
          query: unwrap(track.value).query,
          attribute: unwrap(attribute),
          signal,
        }),
      ...options,
      // Use the previous data if the query is still loading to prevent getting undefined
      placeholderData: (
        previousData: StrategyTrackResponseDTO | undefined,
        previousQuery,
      ): StrategyTrackResponseDTO | undefined => {
        if (previousQuery) return previousData;
      },
      enabled,
    }),
    enabled,
  );
};

export function useGetPreviewStrategyKey(
  track: Ref<{ code: string; query: ITrackQuery } | null>,
  attribute: Ref<RegimeAnalysisAttributeDTO | null>,
): readonly [
  {
    readonly scope: 'regime-analysis';
    readonly entity: 'preview-strategy';
    readonly track: Ref<{ code: string; query: ITrackQuery } | null>;
    readonly attribute: Ref<RegimeAnalysisAttributeDTO | null>;
  },
] {
  return keys.previewStrategy(track, attribute);
}

export function useGetSignalProfileKey(request: Ref<RegimeSignalProfileRequestDTO | null>): readonly [
  {
    readonly scope: 'regime-analysis';
    readonly entity: 'signal-profile';
    readonly request: Ref<RegimeSignalProfileRequestDTO | null>;
  },
] {
  return keys.signalProfile(request);
}

export const usePreviewRegimeOnPortfolio = (
  track: Ref<{
    slug: string;
    query: IPortfolioTreeTracksQuery;
  } | null>,
  attribute: Ref<RegimeAnalysisAttributeDTO | null>,
  options: VQQueryOptions<IPortfolioTreeTrackResponse[]> = {},
): UseQueryReturnType<IPortfolioTreeTrackResponse[], Error> => {
  return useQuery<IPortfolioTreeTrackResponse[], Error>({
    queryKey: keys.previewPortfolio(track, attribute),
    queryFn: ({ signal }): Promise<IPortfolioTreeTrackResponse[]> =>
      previewRegimeOnPortfolio(unwrap(track.value).slug, unwrap(track.value).query, unwrap(attribute), signal),
    ...options,
    enabled: chainEnabled(
      options.enabled,
      computed(() => !!track.value && !!attribute.value),
      enforceMinimumDate(unwrap(track.value).query),
    ),
  });
};

/**
 * We only want to show the toast after both the mutation AND the query invalidation
 */
export const useCreateRegimeSet = defineMutation({
  mutationFn(options: { payload: RegimeAnalysisAttributeDTO }) {
    return postRegime(options.payload);
  },
  invalidateCache(client) {
    return client.invalidateQueries({ queryKey: keys.all() });
  },
});
export const useUpdateRegimeSet = defineMutation({
  mutationFn({ id, payload }: { id: string; payload: RegimeAnalysisAttributeDTO }) {
    return updateRegime(id, payload);
  },
  // after the mutation has run, invalidate the cache
  invalidateCache(client, { id }) {
    // invalidate other queries using this regimeId, but do not wait for them
    client.invalidateQueries({ queryKey: [{ regimeId: id }] });

    // only depend on updating the regime list before returning to allow the modal to close
    return client.invalidateQueries({ queryKey: keys.all() });
  },
});
export const useDeleteRegimeSet = defineMutation({
  mutationFn: (id: string) => deleteRegime(id),
  invalidateCache(client) {
    return client.invalidateQueries({ queryKey: keys.all() });
  },
});

export const useGetRegimeSets = (
  options: VQQueryOptions = {},
): UseQueryReturnType<RegimeAnalysisAttributeResponseDTO[], Error> => {
  return useQuery<RegimeAnalysisAttributeResponseDTO[], Error>({
    queryKey: keys.list(),
    queryFn: ({ signal }) => getRegimes(signal),
    ...options,
    select: select.regimeSets,
  });
};

/**
 * Fetch available signal options for regime analysis dropdowns
 */
export const useGetSignalOptions = (
  options: VQQueryOptions<RegimeSignalOptionsResponseDTO> = {},
): LoadingWhenEnabledQRT<RegimeSignalOptionsResponseDTO, Error> => {
  return useLoadingWhenEnabledQuery(
    useQuery<RegimeSignalOptionsResponseDTO, Error>({
      queryKey: keys.signalOptions(),
      queryFn: ({ signal }) => getSignalOptions(signal),
      ...options,
    }),
    chainEnabled(options.enabled),
  );
};

/**
 * Fetch signal profile data including raw track and KDE distribution
 * for the signal-based regime preview/configuration UI
 */
export const useGetSignalProfile = (
  request: Ref<RegimeSignalProfileRequestDTO | null>,
  options: VQQueryOptions<RegimeSignalProfileResponseDTO> = {},
): LoadingWhenEnabledQRT<RegimeSignalProfileResponseDTO, Error> => {
  const enabled = chainEnabled(
    options.enabled,
    computed(() => request.value !== null),
  );

  return useLoadingWhenEnabledQuery(
    useQuery<RegimeSignalProfileResponseDTO, Error>({
      queryKey: keys.signalProfile(request),
      queryFn: ({ signal }) => getSignalProfile(unwrap(request), signal),
      ...options,
      enabled,
      placeholderData: (
        previousData: RegimeSignalProfileResponseDTO | undefined,
        previousQuery,
      ): RegimeSignalProfileResponseDTO | undefined => {
        if (previousQuery) return previousData;
      },
    }),
    enabled,
  );
};
